/**
 * Загрузка файла напрямую в хранилище (agents/api-media-assets.md §2).
 *
 * API только выдаёт подписанную ссылку и регистрирует запись — сам файл
 * через сервер не идёт: основное видео персоны это 1-3 минуты с телефона.
 */
import type { AssetKind, AssetView } from '@alterium/shared';
import { assetsApi } from '../../api/assets';
import { i18n } from '../../i18n';

/**
 * Выше этого размера файл целиком в память не читаем: на телефоне это
 * гарантированный отказ вкладки. Считаем отпечаток по краям и размеру —
 * для дедупликации этого достаточно, содержимое сервер всё равно не сверяет.
 */
const FULL_HASH_LIMIT_BYTES = 200 * 1024 * 1024;
const SAMPLE_BYTES = 8 * 1024 * 1024;

const toHex = (buffer: ArrayBuffer): string =>
  [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');

export async function fingerprint(file: File): Promise<string> {
  if (file.size <= FULL_HASH_LIMIT_BYTES) {
    return toHex(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
  }

  const head = await file.slice(0, SAMPLE_BYTES).arrayBuffer();
  const tail = await file.slice(Math.max(0, file.size - SAMPLE_BYTES)).arrayBuffer();
  const meta = new TextEncoder().encode(`${file.size}:${file.name}:${file.lastModified}`);

  const combined = new Uint8Array(head.byteLength + tail.byteLength + meta.byteLength);
  combined.set(new Uint8Array(head), 0);
  combined.set(new Uint8Array(tail), head.byteLength);
  combined.set(meta, head.byteLength + tail.byteLength);

  return toHex(await crypto.subtle.digest('SHA-256', combined));
}

export function kindForFile(file: File): AssetKind | null {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type === 'application/pdf' || file.type.startsWith('text/')) return 'document';
  return null;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

export async function uploadFile(
  file: File,
  options: { kind?: AssetKind; onProgress?: (progress: UploadProgress) => void } = {},
): Promise<AssetView> {
  const kind = options.kind ?? kindForFile(file);
  if (!kind) throw new Error(i18n.t('upload.error.format'));

  const intent = await assetsApi.uploadIntent({
    kind,
    mime: file.type,
    bytes: file.size,
    checksum: await fingerprint(file),
    originalName: file.name,
  });

  // Такой файл уже загружен — второй раз платить трафиком незачем (§24).
  if (intent.duplicate || !intent.uploadUrl) {
    return assetsApi.get(intent.assetId);
  }

  await putToStorage(intent.uploadUrl, file, intent.headers, options.onProgress);
  return assetsApi.complete(intent.assetId);
}

/**
 * XHR, а не fetch: нужен прогресс загрузки, а у fetch его нет
 * (ReadableStream для upload поддержан не везде).
 */
function putToStorage(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress?: (progress: UploadProgress) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);

    // Подпись считается вместе с заголовками: отправляем ровно те, что вернул API.
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value);
    }

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total });
    });

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new Error(i18n.t('upload.error.failed')));
    });
    request.addEventListener('error', () =>
      reject(new Error(i18n.t('upload.error.network'))),
    );
    request.addEventListener('abort', () => reject(new Error(i18n.t('upload.error.aborted'))));

    request.send(file);
  });
}
